// TI-1099 — who is this participant: the interview CANDIDATE or the RECRUITER hosting them?
//
// video-streaming-server derives this server-side at mint time (from the JD-funnel invite
// token / UM bearer) and stamps it into the SIGNED join token as `attributes.role`. We read it
// back out of the token this app already holds.
//
// Why decode the token rather than read Participant.attributes: this app runs livekit-client
// v1.15, which has no participant-attributes API at all (it landed in v2.x). Reading
// `localParticipant.attributes.role` would be undefined for every participant, which would
// silently switch identity verification OFF for every candidate — the exact failure this
// change exists to prevent. Upgrading livekit-client v1→v2 is a major migration and is not
// this ticket.
//
// No signature verification here, and none is needed: this only decides which UI to render.
// The real boundary is server-side — integrity-service requires a recorded consent decision
// before it will accept any biometric frame, and VSS signs the token.

export const ROLE_CANDIDATE = "candidate";
export const ROLE_RECRUITER = "recruiter";

/** Decode a JWT payload without verifying it. Returns null on anything malformed. */
function decodePayload(token) {
  if (typeof token !== "string") return null;
  const parts = token.split(".");
  if (parts.length < 2) return null;
  try {
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    const binary = window.atob(padded);
    // Decode as UTF-8 — a participant identity may carry non-ASCII characters, and a naive
    // latin1 read would throw here and lose the role claim with it.
    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes));
  } catch (_) {
    return null;
  }
}

/**
 * Resolve the participant role from a LiveKit access token.
 *
 * Fails SAFE toward `candidate`: only an explicit `recruiter` claim suppresses the consent
 * modal and identity monitoring. A missing claim — an older VSS build that does not stamp the
 * attribute yet, or a malformed token — keeps today's behaviour, where everyone is treated as
 * the candidate. That is what makes this deploy-order independent: shipping this app before
 * the VSS half degrades to the current behaviour instead of silently disabling verification.
 *
 * @param {string|null|undefined} accessToken the LiveKit join token minted by VSS
 * @returns {'candidate' | 'recruiter'}
 */
export function resolveParticipantRole(accessToken) {
  const claims = decodePayload(accessToken);
  const role = claims?.attributes?.role;
  return role === ROLE_RECRUITER ? ROLE_RECRUITER : ROLE_CANDIDATE;
}
